/**
 * Archies — gender gallery filter + OWNED pagination (native Horizon slideshow).
 *
 * FOUNDATION (unchanged, audited-sound): slides are tagged server-side with
 * `media-gender--{women|men|shared}` in blocks/_product-media-gallery.liquid. The active gender lives on
 * body[data-pdp-gender] (set by tabs-ext.js, survives the <main> morph on a combined-listing colour swap).
 * We set slideshow-slide[hidden] on the off-gender slides; slideshow.js's `get slides()` excludes [hidden],
 * so the native slideshow filters itself. This is Horizon's own documented filtering seam and degrades
 * safely (if it ever changes, both genders show — ugly, not broken).
 *
 * PAGINATION (rebuilt 2026-08-19 after the audit): we no longer patch Horizon core's dots. Core drives its
 * dots via positional `aria-selected` (invalid on a <button>) + CSS scroll-timeline animations + an
 * `on:click="/select/<rawIndex>"` that indexes VISIBLE slides — three things that fight a filtered slide
 * set and one (aria-selected) that fails the Pa11y gate. Instead we HIDE core's dots (CSS) and render OUR
 * OWN pagination: one `.swiper-pagination-bullet` <button> per VISIBLE slide (reusing the swiper's bullet
 * styling — one shared standard), click → slideshow.select({id}) (immune to index drift), active state via
 * IntersectionObserver on the slides (no dependency on core's aria-selected/scroll-timeline). All accessible
 * strings come from `t:` keys via window.ArchiesCarouselI18n (see snippets/custom-scripts.liquid).
 * See UPGRADE.md.
 */
(function () {
  'use strict';

  var GALLERY_SEL = 'media-gallery, .product-information__media';

  function activeGender() {
    return document.body.getAttribute('data-pdp-gender') === 'men' ? 'men' : 'women';
  }

  // ---- i18n (translated strings emitted from Liquid t: keys; English fallbacks only if config missing) --
  function i18n() { return window.ArchiesCarouselI18n || {}; }
  function slideLabel(n, total) {
    var tpl = i18n().slideStatus || 'Slide __N__ of __M__';
    return tpl.replace('__N__', n).replace('__M__', total);
  }

  // ---- gender filter: hide off-gender slides + thumbnails (the sound foundation) --------------------
  // Returns the ordered list of VISIBLE slide elements (or []).
  function filterSlides(gallery) {
    var slides = [].slice.call(gallery.querySelectorAll('slideshow-slide'));
    if (!slides.length) return [];

    var other = (activeGender() === 'men' ? 'women' : 'men');
    var otherClass = 'media-gender--' + other;
    var hiddenIds = {};
    var visible = [];

    slides.forEach(function (slide) {
      if (slide.classList.contains(otherClass)) {
        slide.setAttribute('hidden', '');
        slide.removeAttribute('reveal');
        var sid = slide.getAttribute('slide-id');
        if (sid) hiddenIds[sid] = true;
      } else {
        slide.removeAttribute('hidden');
        slide.removeAttribute('reveal');
        visible.push(slide);
      }
    });

    // Desktop 'thumbnails' rail carries no slide-id — tag each from its own <img alt> (same convention as
    // the slides; check 'women' before 'men' because "women" contains "men"). Case-insensitive, like the
    // Liquid readers, so a lower-case tag can't split the thumbnail rail from the slides.
    gallery.querySelectorAll('.slideshow-controls__thumbnail').forEach(function (thumb) {
      var img = thumb.querySelector('img');
      var alt = img ? (img.getAttribute('alt') || '').toLowerCase() : '';
      var g = alt.indexOf('women') !== -1 ? 'women' : (alt.indexOf('men') !== -1 ? 'men' : 'shared');
      thumb.toggleAttribute('hidden', g === other);
    });

    return visible;
  }

  // ---- carousel semantics + live region (WCAG 4.1.2 / 4.1.3) ---------------------------------------
  function ensureCarouselSemantics(gallery) {
    var comp = gallery.querySelector('slideshow-component');
    if (comp) {
      if (!comp.getAttribute('role')) comp.setAttribute('role', 'region');
      if (!comp.hasAttribute('aria-roledescription')) {
        comp.setAttribute('aria-roledescription', i18n().carousel || 'carousel');
      }
      if (!comp.hasAttribute('aria-label')) {
        // Prefer the product's display heading (already localized). Fall back to a t: gallery label.
        var titleEl = document.querySelector('.product-information h1, .product-information .product-title, h1.product-title');
        comp.setAttribute('aria-label',
          (titleEl && titleEl.textContent.trim()) || i18n().gallery || 'Product gallery');
      }
    }
    if (!gallery.__archiesLive) {
      var lr = document.createElement('div');
      lr.setAttribute('aria-live', 'polite');
      lr.setAttribute('aria-atomic', 'true');
      lr.className = 'archies-gallery-live';
      lr.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);clip-path:inset(50%);white-space:nowrap;';
      // On the gallery itself, never inside slideshow-controls: on mobile the first slideshow-controls is
      // display:none (.mobile:hidden), and nothing inside a display:none element is ever announced — VoiceOver
      // stayed silent on "Slide 2 of 14" and only said "current item" (Ethan's device test, 2026-10-16).
      gallery.appendChild(lr);
      gallery.__archiesLive = lr;
    }
  }

  // ---- active-bullet marking (called by the IntersectionObserver) -----------------------------------
  function setActiveBullet(gallery, idx, silent) {
    var bullets = gallery.__archiesBullets || [];
    if (!bullets.length || idx < 0 || idx >= bullets.length) return;
    if (gallery.__archiesActiveIdx === idx) return; // guard: no redundant writes / re-announce
    gallery.__archiesActiveIdx = idx;
    for (var i = 0; i < bullets.length; i++) {
      if (i === idx) {
        bullets[i].classList.add('swiper-pagination-bullet-active');
        bullets[i].setAttribute('aria-current', 'true'); // valid on <button>, unlike aria-selected
      } else {
        bullets[i].classList.remove('swiper-pagination-bullet-active');
        bullets[i].removeAttribute('aria-current');
      }
    }
    // When the strip overflows (many images), keep the active dot in view by scrolling the STRIP only —
    // scrollBy on the <ol> never scrolls the page (unlike scrollIntoView, which walks ancestors).
    var ol = bullets[idx].closest('.archies-gallery-pagination');
    if (ol && ol.scrollWidth > ol.clientWidth + 1) {
      var br = bullets[idx].getBoundingClientRect();
      var or = ol.getBoundingClientRect();
      ol.scrollBy({ left: (br.left + br.width / 2) - (or.left + or.width / 2), behavior: 'smooth' });
    }

    // Announce, but not on the initial build (WCAG 4.1.3 — avoid speaking on page load).
    if (!silent && gallery.__archiesLive) {
      var msg = slideLabel(idx + 1, bullets.length);
      if (gallery.__archiesLive.textContent !== msg) gallery.__archiesLive.textContent = msg;
    }
  }

  // ---- build OUR pagination from the visible slides -------------------------------------------------
  // We REPURPOSE core's existing dot <ol> (`.slideshow-controls__dots`) rather than injecting a new
  // container + hiding core's: keeping the <ol> in layout means no collapse-then-inject double shift
  // (CLS / Rule 7). Clearing its <li>s also detaches core's invalid positional `aria-selected` buttons
  // from the live tree (core's `set current` then writes to detached nodes = harmless, axe never sees it),
  // and drops core's `animation-timeline` scroll-driven active styling with them. Our bullets are the same
  // `.swiper-pagination-bullet` the swiper uses → one shared spec, gallery and swiper read identically.
  function buildPagination(gallery, visibleSlides) {
    var ol = gallery.querySelector('.slideshow-controls__dots');
    if (!ol || !visibleSlides.length) return; // counter/thumbnail presentations have no dot row — skip
    var slideshow = gallery.querySelector('slideshow-component');
    ol.classList.add('archies-gallery-pagination');

    // Skip a rebuild when the visible slide set is identical to what's already rendered. Avoids a needless
    // clear→refill reflow (the "condense then spread" flash) on repeat calls, and — critically — keeps the
    // colour-swap MutationObserver (below) from looping: a no-op run makes NO childList mutation.
    var sig = visibleSlides.map(function (s) { return s.getAttribute('slide-id') || ''; }).join(',');
    if (ol.__archiesSig === sig && ol.querySelectorAll('.swiper-pagination-bullet').length === visibleSlides.length) {
      gallery.__archiesBullets = [].slice.call(ol.querySelectorAll('.swiper-pagination-bullet'));
      return; // already built for this exact set; IntersectionObserver + active state still valid
    }

    var hadFocus = ol.contains(document.activeElement); // WCAG 2.4.3 rescue across rebuild
    // Build every bullet in a fragment, then swap in ONE atomic op (single reflow, no empty-row flash).
    var frag = document.createDocumentFragment();
    visibleSlides.forEach(function (slide, k) {
      var li = document.createElement('li');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'swiper-pagination-bullet';
      b.setAttribute('aria-label', slideLabel(k + 1, visibleSlides.length));
      var sid = slide.getAttribute('slide-id');
      b.addEventListener('click', function () {
        if (slideshow && typeof slideshow.select === 'function' && sid) {
          try { slideshow.select({ id: sid }); } catch (e) { /* best effort */ }
        }
      });
      li.appendChild(b);
      frag.appendChild(li);
    });
    ol.replaceChildren(frag); // atomic — removes core's <li>s + adds ours in a single reflow
    ol.__archiesSig = sig;
    gallery.__archiesBullets = [].slice.call(ol.querySelectorAll('.swiper-pagination-bullet'));
    gallery.__archiesActiveIdx = -1; // force the first setActiveBullet to paint

    // Active tracking: whichever visible slide is most in view owns the active bullet. IntersectionObserver
    // (not a scroll handler) → no per-frame main-thread work, and no dependency on core's dot state.
    if (gallery.__archiesIO) gallery.__archiesIO.disconnect();
    var scroller = gallery.querySelector('slideshow-slides');
    if (scroller) {
      var ratios = [];
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var i = visibleSlides.indexOf(en.target);
          if (i >= 0) ratios[i] = en.intersectionRatio;
        });
        var bestI = -1, best = -1;
        for (var i = 0; i < visibleSlides.length; i++) {
          var r = ratios[i] || 0;
          if (r > best) { best = r; bestI = i; }
        }
        if (bestI >= 0) setActiveBullet(gallery, bestI);
      }, { root: scroller, threshold: [0.25, 0.5, 0.75, 1] });
      visibleSlides.forEach(function (s) { io.observe(s); });
      gallery.__archiesIO = io;
    }

    setActiveBullet(gallery, 0, true); // paint dot 0 active, silently (no load announcement)
    if (hadFocus && gallery.__archiesBullets[0]) gallery.__archiesBullets[0].focus();
  }

  // ---- desktop: click-and-drag to scroll the vertical thumbnail rail --------------------------------
  // Touch already drag-scrolls natively; this adds mouse drag-to-scroll for fine pointers. A real drag
  // must NOT also select a thumbnail, so after a drag we swallow the trailing click at document-capture
  // (beats Horizon's delegated on:click). Attached once per rail; re-attaches after a colour-swap morph
  // replaces the rail node.
  function wireThumbnailDrag(gallery) {
    var rail = gallery.querySelector('.slideshow-controls__thumbnails-container');
    if (!rail || rail.__archiesThumbDrag) return;
    if (!(window.matchMedia && window.matchMedia('(pointer: fine)').matches)) return; // desktop mouse only
    rail.__archiesThumbDrag = true;
    rail.classList.add('archies-thumb-draggable'); // CSS grab cursor + no-drag (native-carousel-ext.css)

    // Kill the browser's native image drag-and-drop (dragging a thumbnail <img> to the desktop/clipboard).
    // It fires on `dragstart`, which pointer preventDefault doesn't stop — in a scroll rail it's never wanted.
    rail.addEventListener('dragstart', function (e) { e.preventDefault(); });

    var down = false, capturing = false, startY = 0, startTop = 0, moved = 0;
    rail.addEventListener('pointerdown', function (e) {
      if (e.button !== 0) return;
      down = true; capturing = false; moved = 0; startY = e.clientY; startTop = rail.scrollTop;
      // NB: do NOT capture the pointer here — capturing on a plain click suppresses the thumbnail's
      // click. Only capture once movement crosses the drag threshold (below).
    });
    rail.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dy = e.clientY - startY;
      if (Math.abs(dy) > moved) moved = Math.abs(dy);
      if (moved > 4) {                       // now it's a drag, not a click
        if (!capturing) {
          capturing = true;
          rail.classList.add('is-grabbing');
          try { rail.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
        }
        rail.scrollTop = startTop - dy;      // rail scrolls vertically
        e.preventDefault();                  // suppress text/image selection while dragging
      }
    });
    var end = function (e) {
      if (!down) return;
      down = false;
      rail.classList.remove('is-grabbing');
      if (capturing) { try { rail.releasePointerCapture(e.pointerId); } catch (err) {} }
      if (moved > 4) {
        // Swallow the click that fires after a drag so it doesn't select the thumbnail under the pointer.
        // Scoped to the rail + one-shot so it can never eat a deliberate click elsewhere.
        var swallow = function (ev) {
          if (rail.contains(ev.target)) { ev.preventDefault(); ev.stopPropagation(); }
          document.removeEventListener('click', swallow, true);
        };
        document.addEventListener('click', swallow, true);
        setTimeout(function () { document.removeEventListener('click', swallow, true); }, 300);
      }
    };
    rail.addEventListener('pointerup', end);
    rail.addEventListener('pointercancel', end);
  }

  // ---- orchestration -------------------------------------------------------------------------------
  function applyToGallery(gallery) {
    var visible = filterSlides(gallery);
    var slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && visible[0] && typeof slideshow.select === 'function') {
      var sid = visible[0].getAttribute('slide-id');
      if (sid) {
        try { slideshow.select({ id: sid }, undefined, { animate: false }); } catch (e) { /* best effort */ }
      }
    }
    ensureCarouselSemantics(gallery);
    buildPagination(gallery, visible);
    wireThumbnailDrag(gallery);
  }

  function run() {
    document.querySelectorAll(GALLERY_SEL).forEach(applyToGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-filter + rebuild pagination when the gender toggle flips body[data-pdp-gender].
  new MutationObserver(run).observe(document.body, {
    attributes: true,
    attributeFilter: ['data-pdp-gender'],
  });

  // Size change: media-gallery.js leaves the (identical-media) gallery in place and gender is unchanged,
  // so nothing needs re-filtering — re-touching the slides only risks a reflow that resets scroll. [no-op]

  // Colour / connected-product swaps morph <main>. Guard: size radios ALSO carry
  // data-connected-product-url but EMPTY — only a real colour swap (non-empty URL) re-filters + resets to
  // the first visible slide (a same-product SIZE change must NOT, or the carousel jumps to slide 0 — the
  // "gallery size-flash" bug). See UPGRADE.md. Rather than the old fixed 600/1400ms double-fire (two
  // staggered reflows, and it missed slow morphs), arm a SCOPED observer only on the colour-swap click and
  // run ONCE when the morph settles — timing-agnostic, single reflow, and still size-change-safe because
  // it's armed only by a real swatch click, not by every mutation.
  document.addEventListener('click', function (e) {
    var swatch = e.target.closest && e.target.closest('[data-connected-product-url]');
    if (!swatch || !swatch.getAttribute('data-connected-product-url')) return;
    var gal = document.querySelector(GALLERY_SEL);
    var host = (gal && gal.closest('main, #MainContent, .shopify-section')) || document.body;
    var done = false, settle = null;
    var mo = new MutationObserver(function () {
      clearTimeout(settle);
      settle = setTimeout(function () { if (done) return; done = true; mo.disconnect(); run(); }, 150);
    });
    mo.observe(host, { childList: true, subtree: true });
    setTimeout(function () { if (!done) { done = true; mo.disconnect(); run(); } }, 3000); // safety cap
  }, true);
})();
